/**
 * engine/youtube.ts — the one and only contact surface with YouTube's player API.
 *
 * Task 13. `spec.md` «Видео»: «YouTube играет через официальный IFrame Player API … с домена
 * `youtube-nocookie.com` … Скрипт YouTube запрашивается только при открытии вкладки „Видео“.
 * Если скрипт YouTube не загрузился за 8 секунд, или плеер вернул ошибку „встраивание
 * запрещено“, приложение показывает сообщение „YouTube не открывается без VPN“». And
 * «Швы»: «обёртка плеера YouTube с интерфейсом „загрузи, играй, пауза, событие состояния“, за
 * которой в тестах стоит заглушка». This file is that wrapper. Nothing else in `apps/web`
 * names `YT`, `onYouTubeIframeAPIReady` or the address of the script — `test/youtube.test.ts`
 * reads the sources to hold it there.
 *
 * ## The interface
 *
 * A {@link YoutubeFactory} puts ONE player into a host element and returns its handle:
 * {@link YoutubePlayer.load} (this video, and start it), `play`, `pause`, `destroy`. Everything
 * the player says comes back through one callback as a {@link YoutubeEvent}: ready, a state,
 * «встраивание запрещено», another error, or «not ready in eight seconds». Commands given
 * before the player is ready are kept and carried out the moment it is.
 *
 * Task 74 added what the probe of clips in a row asks (`apps/web/proba/video-probe.ts`): YouTube's
 * own list ({@link YoutubePlayer.loadList}), a seek, the clip's length, whether it plays muted, and
 * its place in a list. Asked before the player is ready they answer «nothing yet» — 0, sound on,
 * no list — and a seek is dropped: there is nothing in the frame to move. And one event more,
 * `blocked`: the API's own `onAutoplayBlocked`, «the browser blocks autoplay or scripted video
 * playback» — a start by `loadVideoById`, `loadPlaylist` or `playVideo` the browser refused.
 *
 * ## How the frame is built
 *
 * The API documents two ways in, and the second one is used: the frame is created HERE,
 * pointing at `youtubeEmbedUrl(id, origin)` of `@fdm/shared` — the privacy-enhanced host,
 * `enablejsapi=1`, our origin — and then handed to `new YT.Player(frame)`. The API talks to a
 * frame it was given through that frame's own origin (checked in its widget script, 2026-09-26),
 * so the host is written once, in the shared package, and the page policy (`frame-src`) names
 * the same one. Letting the API build the frame would use the host of its own configuration
 * instead, `www.youtube.com`, and no frame from there is allowed.
 *
 * ## «Не открывается без VPN» — the eight seconds
 *
 * From the moment a player is asked for to the moment the frame says it is ready. Without a
 * VPN YouTube's hosts are blocked or starved, and neither the script nor the frame ever
 * arrives — there is no error to catch, only silence, and the deadline is how silence becomes
 * an answer. A script the network refused outright fails at once. A player that turns up late
 * still reports `ready`: the message goes away the moment the clip can really play.
 */

import { youtubeEmbedUrl } from '@fdm/shared';

/* ────────────────────────────────────────────────────────────────────────────
 * The interface
 * ──────────────────────────────────────────────────────────────────────────── */

/** The player's own states, named. */
export type YoutubeState = 'unstarted' | 'playing' | 'paused' | 'buffering' | 'ended' | 'cued';

/** Everything a player says. */
export type YoutubeEvent =
  | { readonly type: 'ready' }
  | { readonly type: 'state'; readonly state: YoutubeState }
  /** Errors 101 and 150: the owner of the video does not allow it to be played elsewhere. */
  | { readonly type: 'refused' }
  /** Any other error: a removed video (100), a bad id (2), the player's own failure (5). */
  | { readonly type: 'failed'; readonly code: number }
  /** Task 74: the browser refused to start the clip — `onAutoplayBlocked`. See the header. */
  | { readonly type: 'blocked' }
  /** The script or the frame did not arrive within {@link YOUTUBE_READY_TIMEOUT_MS}. */
  | { readonly type: 'unavailable' };

/** One player in one host. */
export interface YoutubePlayer {
  /** Puts `videoId` into the frame and starts it. Called from a tap. */
  load(videoId: string): void;
  /** Task 74: YouTube's own list, from `index`; the player goes on to the next by itself. */
  loadList(videoIds: readonly string[], index: number): void;
  /** Task 74: moves the clip in the frame to `seconds` from its start. */
  seek(seconds: number): void;
  /** Task 74: the length of the clip in the frame, in seconds; 0 while the player does not know. */
  duration(): number;
  /** Task 74: whether it plays without sound — a phone may start a clip only muted. */
  muted(): boolean;
  /** Task 74: which clip of a list is in the frame, from 0; -1 — there is no list. */
  listIndex(): number;
  play(): void;
  pause(): void;
  /** Takes the frame out of the page: the sound stops with it. The handle is dead after. */
  destroy(): void;
}

export interface YoutubeOptions {
  /** The element the frame goes into. */
  readonly host: HTMLElement;
  /** The video shown first — shown, not started: a launch of the tab is not a tap. */
  readonly videoId: string;
  readonly onEvent: (event: YoutubeEvent) => void;
}

/** Builds one player. The production one is {@link createYoutubeFactory}; a test installs a stub. */
export type YoutubeFactory = (options: YoutubeOptions) => YoutubePlayer;

/* ────────────────────────────────────────────────────────────────────────────
 * The real one
 * ──────────────────────────────────────────────────────────────────────────── */

/** The address of YouTube's player API script. The page policy allows its host. */
export const YOUTUBE_API_SCRIPT = 'https://www.youtube.com/iframe_api';

/** `spec.md` «Видео»: «не загрузился за 8 секунд». */
export const YOUTUBE_READY_TIMEOUT_MS = 8000;

/** The part of `YT.Player` this file calls. */
interface YtPlayer {
  loadVideoById(videoId: string): void;
  loadPlaylist(playlist: string[], index: number): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getDuration(): number;
  isMuted(): boolean;
  getPlaylistIndex(): number;
  playVideo(): void;
  pauseVideo(): void;
  destroy(): void;
}

interface YtEvent {
  readonly data: number;
}

interface YtPlayerOptions {
  readonly events: {
    readonly onReady: () => void;
    readonly onStateChange: (event: YtEvent) => void;
    readonly onError: (event: YtEvent) => void;
    readonly onAutoplayBlocked: () => void;
  };
}

type YtPlayerClass = new (frame: HTMLIFrameElement, options: YtPlayerOptions) => YtPlayer;

/** What the script leaves on `window`, and the one callback it calls when it has. */
interface YtGlobal {
  YT?: { readonly Player?: YtPlayerClass | undefined } | undefined;
  onYouTubeIframeAPIReady?: (() => void) | undefined;
}

/** `YT.PlayerState`, by number: the API's own table. */
const STATES: ReadonlyMap<number, YoutubeState> = new Map([
  [-1, 'unstarted'],
  [0, 'ended'],
  [1, 'playing'],
  [2, 'paused'],
  [3, 'buffering'],
  [5, 'cued'],
]);

/** 101 and 150 are one refusal under two numbers: «embedding is not allowed». */
const REFUSED_CODES: ReadonlySet<number> = new Set([101, 150]);

export interface YoutubeEnvironment {
  /** Defaults to the page's `window`. */
  readonly window?: (Window & YtGlobal) | undefined;
  readonly timeoutMs?: number | undefined;
}

/**
 * The real factory. The script is asked for on the FIRST player and never before — importing
 * this module requests nothing — and once for all the players after it. A script the network
 * refused is forgotten, so the next opening of the tab asks again.
 */
export function createYoutubeFactory(env: YoutubeEnvironment = {}): YoutubeFactory {
  const win: Window & YtGlobal = env.window ?? window;
  const timeoutMs = env.timeoutMs ?? YOUTUBE_READY_TIMEOUT_MS;
  let loading: Promise<YtPlayerClass> | undefined;

  const playerClass = (): YtPlayerClass | undefined => win.YT?.Player;

  function loadApi(): Promise<YtPlayerClass> {
    const ready = playerClass();
    if (ready !== undefined) return Promise.resolve(ready);
    loading ??= new Promise<YtPlayerClass>((resolve, reject) => {
      // Chained, never replaced: the page may hold a handler of somebody else's.
      const previous = win.onYouTubeIframeAPIReady;
      win.onYouTubeIframeAPIReady = (): void => {
        previous?.();
        const loaded = playerClass();
        if (loaded !== undefined) resolve(loaded);
      };
      const script = win.document.createElement('script');
      script.src = YOUTUBE_API_SCRIPT;
      script.async = true;
      script.addEventListener('error', () => {
        script.remove();
        loading = undefined;
        reject(new Error('the YouTube player script could not be loaded'));
      });
      win.document.head.appendChild(script);
    });
    return loading;
  }

  return ({ host, videoId, onEvent }: YoutubeOptions): YoutubePlayer => {
    let destroyed = false;
    let ready = false;
    let player: YtPlayer | undefined;
    let frame: HTMLIFrameElement | undefined;
    /** A clip or a list asked for before the player was ready: the last one asked. */
    let wanted:
      | { readonly id: string }
      | { readonly ids: readonly string[]; readonly index: number }
      | undefined;
    /** The last of play/pause asked for before the player was ready. */
    let wantedPlay: boolean | undefined;

    const say = (event: YoutubeEvent): void => {
      if (!destroyed) onEvent(event);
    };

    const deadline = win.setTimeout(() => {
      if (!ready) say({ type: 'unavailable' });
    }, timeoutMs);

    loadApi().then(
      (Player) => {
        if (destroyed) return;
        frame = win.document.createElement('iframe');
        frame.src = youtubeEmbedUrl(videoId, win.location.origin);
        frame.title = 'YouTube';
        // `fullscreen` here and not `allowFullscreen` beside it: the browser takes `allow` and
        // warns about the other one.
        frame.allow = 'autoplay; encrypted-media; fullscreen';
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        host.appendChild(frame);
        player = new Player(frame, {
          events: {
            onReady: () => {
              ready = true;
              win.clearTimeout(deadline);
              say({ type: 'ready' });
              if (wanted !== undefined && 'id' in wanted) player?.loadVideoById(wanted.id);
              if (wanted !== undefined && 'ids' in wanted) {
                player?.loadPlaylist([...wanted.ids], wanted.index);
              }
              if (wantedPlay === true) player?.playVideo();
              if (wantedPlay === false) player?.pauseVideo();
              wanted = undefined;
              wantedPlay = undefined;
            },
            onStateChange: (event) => {
              const state = STATES.get(event.data);
              if (state !== undefined) say({ type: 'state', state });
            },
            onError: (event) => {
              say(
                REFUSED_CODES.has(event.data)
                  ? { type: 'refused' }
                  : { type: 'failed', code: event.data },
              );
            },
            onAutoplayBlocked: () => {
              say({ type: 'blocked' });
            },
          },
        });
      },
      () => {
        // Refused by the network: the answer is the same as silence, only sooner.
        if (!ready) say({ type: 'unavailable' });
      },
    );

    return {
      load: (id: string): void => {
        if (ready && player !== undefined) player.loadVideoById(id);
        else wanted = { id };
      },
      loadList: (ids: readonly string[], index: number): void => {
        if (ready && player !== undefined) player.loadPlaylist([...ids], index);
        else wanted = { ids, index };
      },
      seek: (seconds: number): void => {
        if (ready && player !== undefined) player.seekTo(seconds, true);
      },
      duration: (): number => {
        const length = ready && player !== undefined ? player.getDuration() : 0;
        return Number.isFinite(length) && length > 0 ? length : 0;
      },
      muted: (): boolean => ready && player !== undefined && player.isMuted(),
      listIndex: (): number => {
        const index = ready && player !== undefined ? player.getPlaylistIndex() : -1;
        return Number.isInteger(index) && index >= 0 ? index : -1;
      },
      play: (): void => {
        if (ready && player !== undefined) player.playVideo();
        else wantedPlay = true;
      },
      pause: (): void => {
        if (ready && player !== undefined) player.pauseVideo();
        else wantedPlay = false;
      },
      destroy: (): void => {
        if (destroyed) return;
        destroyed = true;
        win.clearTimeout(deadline);
        try {
          player?.destroy();
        } catch {
          // A player that failed to start has nothing to take down; the frame goes anyway.
        }
        frame?.remove();
      },
    };
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The instance the app uses
 * ──────────────────────────────────────────────────────────────────────────── */

let factory: YoutubeFactory | undefined;

/** The factory, created on first use — so the script is asked for when the tab asks, not at load. */
export function youtube(): YoutubeFactory {
  factory ??= createYoutubeFactory();
  return factory;
}

/** Replaces the factory — the web suite's seam (`spec.md` «Швы»: «за которой в тестах стоит заглушка»). */
export function installYoutube(next: YoutubeFactory): void {
  factory = next;
}
